import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Stack } from '@mui/material';

import { useAuth } from '../../core/authContext';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { COLORS } from '../IndividualPlan/tokens';
import { applySavedSisMapping, readSisColumns } from '../Upload/sisFieldMapping';
import { hydrateSisMappings } from './sisMappingApi';
import { DeleteFileModal } from './DeleteFileModal';
import { IntegrationsCard } from './IntegrationsCard';
import { MatchColumnsPromptDialog } from './MatchColumnsPromptDialog';
import { MatchesCallout } from './MatchesCallout';
import { fetchMatches, type MatchesState } from './matchesApi';
import { SetAsideWarnings } from './SetAsideWarning';
import { UploadedDataCard, type SisMatchStatus } from './UploadedDataCard';
import {
  dedupeById,
  hydrateRostersFromUploads,
  importCsvIntoRoster,
  importSisSources,
  isCsvFile,
  isWidaKind,
  rebuildRostersFromCsvs,
  removeFileById,
  toUploadedFiles,
  type UploadKind,
  type UploadedFile,
} from './uploadedFiles';
import {
  deleteUpload,
  fetchUploadCsv,
  fetchUploads,
  saveUpload,
  updateUploadKind,
} from './uploadsApi';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

function sisStatusFromCsv(csvText: string): SisMatchStatus {
  return applySavedSisMapping(readSisColumns(csvText)).kind === 'exact' ? 'matched' : 'required';
}

export function DataIntegrationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [matches, setMatches] = useState<MatchesState | null>(null);
  const [sisStatus, setSisStatus] = useState<Record<string, SisMatchStatus>>({});
  const [pendingDelete, setPendingDelete] = useState<UploadedFile | null>(null);
  const [promptFile, setPromptFile] = useState<UploadedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const pendingSaves = useRef(new Map<string, Promise<UploadedFile>>());
  const removed = useRef(new Set<string>());
  const sourceByKey = useRef(new Map<string, File>());
  const generation = useRef(0);
  const leftRef = useRef(false);
  const matchesRequest = useRef(0);

  useEffect(() => {
    leftRef.current = false;
    return () => {
      leftRef.current = true;
    };
  }, []);

  const refreshMatches = () => {
    const request = ++matchesRequest.current;
    return fetchMatches()
      .then((next) => {
        if (!leftRef.current && request === matchesRequest.current) setMatches(next);
      })
      .catch((e: unknown) => console.warn('data: matches unavailable', e));
  };

  useEffect(() => {
    void refreshMatches();
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([fetchUploads(), hydrateSisMappings()])
      .then(([remote]) => {
        if (!active) return;
        const visible = remote.filter((r) => !removed.current.has(r.id));
        setFiles((prev) => {
          const remoteIds = new Set(visible.map((r) => r.id));
          const localOnly = prev.filter((f) => !remoteIds.has(f.id));
          return [...visible, ...localOnly];
        });
        visible
          .filter((f) => f.kind === 'student_roster')
          .forEach((file) => {
            fetchUploadCsv(file.id)
              .then((content) => {
                if (active) {
                  setSisStatus((prev) => ({ ...prev, [file.id]: sisStatusFromCsv(content.csv) }));
                }
              })
              .catch(() => {});
          });
        void hydrateRostersFromUploads(visible, (id) => removed.current.has(id)).catch(() => {
          if (active) {
            setError("Couldn't load your uploaded data into this session. Please reload the page.");
          }
        });
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const addFiles = (selected: FileList | null) => {
    if (!selected) return;
    setError(null);
    const csvFiles = Array.from(selected).filter(isCsvFile);
    const optimistic = toUploadedFiles(csvFiles).map((entry) => ({ ...entry, clientKey: entry.id }));
    setFiles((prev) => [...prev, ...optimistic]);
    optimistic.forEach((entry, i) => {
      const file = csvFiles[i];
      if (!file) return;
      sourceByKey.current.set(entry.clientKey, file);
      const saving = saveUpload(file, { page: 'data_integrations' }).then((saved) => ({
        ...saved,
        uploadedAt: saved.uploadedAt ?? new Date().toISOString(),
      }));
      pendingSaves.current.set(entry.clientKey, saving);
      saving
        .then((saved) => {
          setFiles((prev) =>
            dedupeById(
              prev.map((f) =>
                (f.clientKey ?? f.id) === entry.clientKey
                  ? { ...saved, kind: f.kind, clientKey: f.clientKey }
                  : f,
              ),
            ),
          );
        })
        .catch(() => {
          pendingSaves.current.delete(entry.clientKey);
          setFiles((prev) => removeFileById(prev, entry.id));
          sourceByKey.current.delete(entry.clientKey);
          setError(`Couldn't save ${file.name}. Please try again.`);
        });
    });
  };

  const selectKind = (target: UploadedFile, kind: UploadKind) => {
    const corrected = target.kind != null && target.kind !== kind;
    const targetKey = target.clientKey ?? target.id;
    setFiles((prev) => prev.map((f) => ((f.clientKey ?? f.id) === targetKey ? { ...f, kind } : f)));
    const saving = pendingSaves.current.get(targetKey);
    const serverId = saving ? saving.then((saved) => saved.id) : Promise.resolve(target.id);
    const kindSaved = serverId
      .then((id) => updateUploadKind(id, kind).then(() => true))
      .catch(() => {
        setError(`Couldn't save the type for ${target.name}. Please try again.`);
        return false;
      });

    const source = sourceByKey.current.get(target.clientKey ?? target.id);
    const csv = source
      ? source.text()
      : serverId.then((id) => fetchUploadCsv(id).then((content) => content.csv));
    const savedYear = saving
      ? saving.then((saved) => saved.schoolYear ?? target.schoolYear)
      : Promise.resolve(target.schoolYear);
    const gen = generation.current;
    const mappingsReady = kind === 'student_roster' ? hydrateSisMappings() : Promise.resolve();
    Promise.all([csv, savedYear, serverId, mappingsReady])
      .then(([text, year]) => {
        if (generation.current === gen && !removed.current.has(target.id)) {
          if (corrected) {
            generation.current += 1;
            const rebuildGen = generation.current;
            setFiles((latest) => {
              void rebuildRosters(latest, rebuildGen);
              return latest;
            });
          } else {
            const anchorMoved = importCsvIntoRoster(text, kind, year);
            if (isWidaKind(kind) && anchorMoved) {
              reimportSisFiles(gen);
            }
          }
          if (kind === 'student_roster') {
            const key = target.clientKey ?? target.id;
            setSisStatus((prev) => ({ ...prev, [key]: sisStatusFromCsv(text) }));
          }
          void kindSaved.then((ok) => {
            if (ok) void refreshMatchesFor();
          });
        }
      })
      .catch(() => setError(`Couldn't read ${target.name}. Please re-add the file.`));
  };

  const refreshMatchesFor = () => {
    if (leftRef.current) return Promise.resolve();
    return refreshMatches();
  };

  const reimportSisFiles = (gen: number) => {
    setFiles((prev) => {
      const sis = prev.filter(
        (f): f is UploadedFile & { kind: UploadKind } =>
          f.kind === 'student_roster' && !removed.current.has(f.id),
      );
      void Promise.allSettled(
        sis.map(async (f) => {
          const source = sourceByKey.current.get(f.clientKey ?? f.id);
          return {
            csv: source ? await source.text() : (await fetchUploadCsv(f.id)).csv,
            kind: f.kind,
            schoolYear: f.schoolYear,
            uploadedAt: f.uploadedAt,
          };
        }),
      ).then((results) => {
        if (generation.current !== gen) return;
        importSisSources(results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : [])));
      });
      return prev;
    });
  };

  const rebuildRosters = async (remaining: UploadedFile[], gen: number) => {
    const typed = remaining.filter((f): f is UploadedFile & { kind: UploadKind } => f.kind != null);
    const results = await Promise.allSettled(
      typed.map(async (f) => {
        const saved = await pendingSaves.current.get(f.clientKey ?? f.id)?.catch(() => null);
        if (saved === null) return null;
        const source = sourceByKey.current.get(f.clientKey ?? f.id);
        const csv = source ? await source.text() : (await fetchUploadCsv(saved?.id ?? f.id)).csv;
        return {
          csv,
          kind: f.kind,
          schoolYear: saved?.schoolYear ?? f.schoolYear,
          uploadedAt: saved?.uploadedAt ?? f.uploadedAt,
        };
      }),
    );
    if (generation.current !== gen) return;
    if (results.some((r) => r.status === 'rejected')) {
      rebuildRostersFromCsvs([]);
      setError("Couldn't refresh every roster after the delete. Please reload the page.");
      return;
    }
    const inputs = results.flatMap((r) =>
      r.status === 'fulfilled' && r.value != null ? [r.value] : [],
    );
    const rebuilt = new Set(typed.map((f) => f.id));
    setFiles((latest) => {
      const late = latest.some(
        (f) => f.kind != null && !rebuilt.has(f.id) && !removed.current.has(f.id),
      );
      if (late) {
        generation.current += 1;
        void rebuildRosters(latest, generation.current);
      } else {
        rebuildRostersFromCsvs(inputs);
      }
      return latest;
    });
  };

  const startMatching = () => {
    const target = promptFile;
    setPromptFile(null);
    if (!target) return;
    const key = target.clientKey ?? target.id;
    const current = files.find((f) => (f.clientKey ?? f.id) === key) ?? target;
    const saving = pendingSaves.current.get(key);
    const serverId = saving ? saving.then((saved) => saved.id) : Promise.resolve(current.id);
    serverId
      .then((id) => navigate(`/data/match/${encodeURIComponent(id)}`))
      .catch(() => setError(`Couldn't open column matching for ${target.name}. Please try again.`));
  };

  const confirmDelete = () => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (!target) return;
    setError(null);
    const saving = pendingSaves.current.get(target.clientKey ?? target.id);
    const serverId = saving ? saving.then((saved) => saved.id) : Promise.resolve(target.id);
    serverId
      .then((id) => deleteUpload(id).then(() => id))
      .then((id) => {
        void refreshMatches();
        removed.current.add(id);
        removed.current.add(target.id);
        sourceByKey.current.delete(target.clientKey ?? target.id);
        generation.current += 1;
        const gen = generation.current;
        setFiles((prev) => {
          const remaining = removeFileById(prev, id);
          queueMicrotask(() => {
            void rebuildRosters(remaining, gen);
          });
          return remaining;
        });
      })
      .catch(() => {
        setError(`Couldn't delete ${target.name}. Please try again.`);
      });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: COLORS.bg }}>
      <title>Data &amp; Integrations</title>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="data" />
      <Box component="main" sx={{ flex: 1, padding: '32px 24px' }}>
        <Stack data-testid="data-content" spacing={3} sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto' }}>
          {error && <Alert severity="error">{error}</Alert>}
          {matches && <SetAsideWarnings files={matches.setAside} userId={user?.id ?? ''} />}
          {matches && <MatchesCallout toReview={matches.toReview} heldOutStudents={matches.heldOutStudents} />}
          <IntegrationsCard status="connected" lastSyncedLabel="Last synced May 4, 2026" />
          <UploadedDataCard
            files={files}
            loading={loading}
            sisStatusByKey={sisStatus}
            setAsideUploadIds={matches?.setAside.map((file) => file.uploadId)}
            onAddFiles={addFiles}
            onRequestDelete={setPendingDelete}
            onSelectKind={selectKind}
            onStartMatching={setPromptFile}
          />
        </Stack>
      </Box>

      <DeleteFileModal
        open={pendingDelete !== null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />

      <MatchColumnsPromptDialog
        open={promptFile !== null}
        onCancel={() => setPromptFile(null)}
        onStartMatching={startMatching}
      />
    </Box>
  );
}
